import { useState } from 'react';

type StudentType = 'urban' | 'rural';
type GradeType = '小学' | '初中' | '高中' | '大学/考研';

interface PairCandidate {
  id: string;
  name: string;
  city: string;
  type: StudentType;
  grade: GradeType;
  subjects: string[];
  bio: string;
  avatar: string;
  online: boolean;
}

const candidates: PairCandidate[] = [
  {
    id: 'p01', name: '陈思远', city: '北京', type: 'urban', grade: '高中',
    subjects: ['数学', '物理'],
    bio: '北京四中高三，数学竞赛生，希望认识乡村朋友，互相学习，一起冲刺高考！',
    avatar: 'https://public.readdy.ai/ai/img_res/29587aa10998fa2be2d8ec206edb0f18.jpg',
    online: true,
  },
  {
    id: 'p02', name: '苗翠花', city: '云南昭通', type: 'rural', grade: '高中',
    subjects: ['英语', '语文'],
    bio: '来自云南山区，英语想提升，也想学习城市同学的表达方式，欢迎加我！',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20teenage%20girl%20student%20Yunnan%20rural%20area%20warm%20smile%20school%20uniform%20clean%20background%20portrait&width=120&height=120&seq=pair002&orientation=squarish',
    online: true,
  },
  {
    id: 'p03', name: '王浩然', city: '上海', type: 'urban', grade: '高中',
    subjects: ['物理', '化学'],
    bio: '上海中学，物化竞赛，想认识踏实努力的乡村同学，互相督促共同进步。',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20teenage%20boy%20student%20Shanghai%20neat%20appearance%20confident%20smile%20clean%20background%20portrait&width=120&height=120&seq=pair003&orientation=squarish',
    online: false,
  },
  {
    id: 'p04', name: '李秀英', city: '甘肃定西', type: 'rural', grade: '高中',
    subjects: ['语文', '历史'],
    bio: '甘肃农村，语文历史不错，想找城市同学帮我提升数学和英语，一起加油！',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20teenage%20girl%20student%20Gansu%20rural%20area%20simple%20dress%20genuine%20smile%20clean%20background%20portrait&width=120&height=120&seq=pair004&orientation=squarish',
    online: true,
  },
  {
    id: 'p05', name: '赵子轩', city: '广州', type: 'urban', grade: '大学/考研',
    subjects: ['考研英语', '数学'],
    bio: '广州备考考研，英语数学都还行，想认识同样备考的乡村同学，一起上岸！',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20university%20student%20Guangzhou%20school%20uniform%20bright%20smile%20clean%20background%20portrait&width=120&height=120&seq=pair005&orientation=squarish',
    online: true,
  },
  {
    id: 'p06', name: '吴小梅', city: '贵州黔东南', type: 'rural', grade: '高中',
    subjects: ['生物', '化学'],
    bio: '贵州苗族女生，生物化学成绩好，想找城市同学学习公众表达和演讲技巧。',
    avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20teenage%20girl%20student%20Guizhou%20rural%20area%20happy%20expression%20school%20uniform%20clean%20background%20portrait&width=120&height=120&seq=pair006&orientation=squarish',
    online: false,
  },
];

interface ApplyForm {
  name: string;
  city: string;
  type: StudentType | '';
  grade: GradeType | '';
  subjects: string;
  expectation: string;
}

export default function PairingSection() {
  const [filterType, setFilterType] = useState<'all' | StudentType>('all');
  const [filterGrade, setFilterGrade] = useState<GradeType | 'all'>('all');
  const [pairedIds, setPairedIds] = useState<Set<string>>(new Set());
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [form, setForm] = useState<ApplyForm>({
    name: '', city: '', type: '', grade: '', subjects: '', expectation: '',
  });

  const filtered = candidates.filter((c) => {
    const typeMatch = filterType === 'all' || c.type === filterType;
    const gradeMatch = filterGrade === 'all' || c.grade === filterGrade;
    return typeMatch && gradeMatch;
  });

  const handlePair = (id: string) => {
    setPairedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowApplyModal(false);
    setShowSuccessModal(true);
    setForm({ name: '', city: '', type: '', grade: '', subjects: '', expectation: '' });
  };

  const grades: GradeType[] = ['小学', '初中', '高中', '大学/考研'];

  return (
    <section className="py-12">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-full px-3 py-1 mb-3">
            <i className="ri-links-line text-red-500 text-xs"></i>
            <span className="text-red-600 text-xs font-semibold">城乡学伴配对</span>
          </div>
          <h2 className="text-2xl font-black text-gray-900">找到你的学伴</h2>
          <p className="text-gray-500 text-sm mt-1">城市与乡村学生双向配对，取长补短，共同成长</p>
        </div>
        <button
          onClick={() => setShowApplyModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          我要申请配对
        </button>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { step: '01', icon: 'ri-file-list-3-line', title: '填写申请', desc: '提交你的基本信息和学习需求' },
          { step: '02', icon: 'ri-search-eye-line', title: '智能匹配', desc: '系统根据年级、科目为你推荐最合适的学伴' },
          { step: '03', icon: 'ri-chat-smile-2-line', title: '开始交流', desc: '互加好友，制定学习计划，共同进步' },
        ].map((s) => (
          <div key={s.step} className="bg-white rounded-2xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl flex-shrink-0">
              <i className={`${s.icon} text-red-600 text-lg`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold text-red-400">STEP {s.step}</span>
                <span className="font-bold text-gray-900 text-sm">{s.title}</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex gap-1 bg-white rounded-full p-1">
          {(['all', 'urban', 'rural'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterType === t ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'all' ? '全部' : t === 'urban' ? '城市学生' : '乡村学生'}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-white rounded-full p-1">
          <button
            onClick={() => setFilterGrade('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              filterGrade === 'all' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            全部年级
          </button>
          {grades.map((g) => (
            <button
              key={g}
              onClick={() => setFilterGrade(g)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filterGrade === g ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl p-5 hover:scale-[1.01] transition-all">
            <div className="flex items-start gap-4 mb-4">
              <div className="relative flex-shrink-0">
                <img src={c.avatar} alt={c.name} className="w-14 h-14 rounded-2xl object-cover object-top" />
                {c.online && (
                  <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-gray-900 text-sm">{c.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    c.type === 'urban' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {c.type === 'urban' ? '城市' : '乡村'}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <i className="ri-map-pin-line text-gray-400 text-xs"></i>
                  <span className="text-gray-500 text-xs">{c.city} · {c.grade}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {c.subjects.map((s) => (
                    <span key={s} className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2">{c.bio}</p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePair(c.id)}
                className={`flex-1 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  pairedIds.has(c.id)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {pairedIds.has(c.id) ? (
                  <><i className="ri-check-line mr-1"></i>已申请配对</>
                ) : (
                  <><i className="ri-links-line mr-1"></i>申请配对</>
                )}
              </button>
              <button className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-full hover:bg-gray-50 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-chat-1-line text-gray-500 text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-gray-900 text-lg">申请学伴配对</h3>
                <p className="text-gray-500 text-xs mt-0.5">填写信息，我们为你智能匹配最合适的学伴</p>
              </div>
              <button
                onClick={() => setShowApplyModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">你的姓名 *</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="请输入姓名"
                    className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">所在城市 *</label>
                  <input
                    required
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="如：北京 / 云南昭通"
                    className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">你是 *</label>
                <div className="flex gap-3">
                  {([['urban', '城市学生'], ['rural', '乡村学生']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm({ ...form, type: val })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        form.type === val
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">年级阶段 *</label>
                <div className="flex flex-wrap gap-2">
                  {grades.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setForm({ ...form, grade: g })}
                      className={`px-4 py-2 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        form.grade === g
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">擅长科目</label>
                <input
                  value={form.subjects}
                  onChange={(e) => setForm({ ...form, subjects: e.target.value })}
                  placeholder="如：数学、物理（用顿号分隔）"
                  className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                  你希望从学伴身上学到什么？
                  <span className="text-gray-400 font-normal ml-1">（最多100字）</span>
                </label>
                <textarea
                  value={form.expectation}
                  onChange={(e) => {
                    if (e.target.value.length <= 100) setForm({ ...form, expectation: e.target.value });
                  }}
                  placeholder="例如：希望学习城市同学的表达方式和学习方法..."
                  rows={3}
                  className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-200 resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-1">{form.expectation.length}/100</p>
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                提交申请，开始配对
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-double-line text-green-600 text-2xl"></i>
            </div>
            <h3 className="font-black text-gray-900 text-xl mb-2">申请提交成功！</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              我们正在为你智能匹配最合适的学伴，预计在 <strong className="text-gray-800">24小时内</strong> 通过站内消息通知你配对结果。
            </p>
            <div className="bg-amber-50 rounded-2xl p-4 mb-6 text-left">
              <div className="flex items-start gap-2">
                <i className="ri-lightbulb-line text-amber-500 text-sm mt-0.5"></i>
                <p className="text-amber-700 text-xs leading-relaxed">
                  配对成功后，记得主动打招呼，制定每周学习计划，城乡互助效果更好哦！
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-gray-900 text-white font-semibold rounded-full cursor-pointer hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              好的，期待配对结果
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
